import React, { useEffect, useState, useCallback, useMemo } from "react";

import { useWsNavigate } from "../hooks/useWsNavigate";
import { Plus, Check, Users, Globe, LayoutGrid, Eye } from "lucide-react";
import { metricsAPI } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import { useAgent } from "../contexts/AgentContext";
import { useToast } from "../contexts/ToastContext";
import { usePageTitle } from "../contexts/PageTitleContext";
import { useCan } from "../hooks/useCan";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

const resultBadge = (rt) => {
  const map = {
    boolean: "secondary",
    rating: "info",
    enum: "warning",
    numeric: "muted",
  };
  return map[rt] || "muted";
};

// ── Add-to-workspace modal ─────────────────────────────────────────────────
const AddMetricModal = ({ metric, agents, onClose, onConfirm, saving }) => {
  const addedGlobal = !!metric.added_global;
  const addedAgentIds = useMemo(
    () => metric.added_agent_ids || [],
    [metric.added_agent_ids],
  );

  // Default to whichever scope makes sense given current state.
  const [scope, setScope] = useState(
    addedGlobal ? "agent" : addedAgentIds.length > 0 ? "agent" : "global",
  );
  // Pre-check agents already linked so the user sees current state and adds more.
  const [agentIds, setAgentIds] = useState(addedAgentIds);

  const toggleAgent = (id) => {
    if (addedAgentIds.includes(id)) return; // can't remove an existing link here
    setAgentIds((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id],
    );
  };

  const newAgentIds = agentIds.filter((id) => !addedAgentIds.includes(id));
  const nothingToDo =
    (scope === "global" && addedGlobal) ||
    (scope === "agent" && newAgentIds.length === 0);
  const disabled = saving || nothingToDo;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onMouseDown={onClose}
    >
      <div
        className="w-full max-w-md rounded-xl border border-border bg-card p-4 shadow-lg"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h2 className="ui-section-title">Add “{metric.name}”</h2>
        <p className="ui-subtitle mt-1">
          Add this metric as a global metric or assign it to specific agents. It
          becomes an editable copy in your workspace.
        </p>

        <div className="mt-4 space-y-2">
          <button
            type="button"
            onClick={() => setScope("global")}
            className={`flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors ${
              scope === "global"
                ? "border-primary bg-primary/5"
                : "border-border hover:bg-muted"
            }`}
          >
            <Globe className="mt-0.5 size-4 text-muted-foreground" />
            <div className="flex-1">
              <div className="flex items-center gap-2 text-sm font-medium text-foreground">
                Global — all agents
                {addedGlobal && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-medium text-emerald-700 border border-emerald-200">
                    <Check className="size-3" /> Added
                  </span>
                )}
              </div>
              <div className="ui-meta">Runs on every agent in this workspace.</div>
            </div>
          </button>

          <button
            type="button"
            onClick={() => setScope("agent")}
            className={`flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors ${
              scope === "agent"
                ? "border-primary bg-primary/5"
                : "border-border hover:bg-muted"
            }`}
          >
            <Users className="mt-0.5 size-4 text-muted-foreground" />
            <div>
              <div className="text-sm font-medium text-foreground">
                Specific agents
              </div>
              <div className="ui-meta">Assign to one or more agents.</div>
            </div>
          </button>
        </div>

        {scope === "agent" && (
          <div className="mt-3 max-h-48 overflow-y-auto rounded-lg border border-border p-1">
            {agents.length === 0 ? (
              <p className="ui-meta p-2">No agents in this workspace yet.</p>
            ) : (
              agents.map((a) => {
                const already = addedAgentIds.includes(a.id);
                return (
                  <label
                    key={a.id}
                    className={`flex items-center gap-2 rounded px-2 py-1.5 text-sm ${
                      already
                        ? "cursor-default opacity-70"
                        : "cursor-pointer hover:bg-muted"
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={agentIds.includes(a.id)}
                      disabled={already}
                      onChange={() => toggleAgent(a.id)}
                      className="rounded border-input"
                    />
                    <span className="truncate text-foreground">{a.name}</span>
                    {already && (
                      <span className="ml-auto inline-flex items-center gap-1 text-[10px] font-medium text-emerald-700">
                        <Check className="size-3" /> Added
                      </span>
                    )}
                  </label>
                );
              })
            )}
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-muted-foreground hover:bg-muted"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={disabled}
            onClick={() => onConfirm({ scope, agentIds })}
            className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {saving
              ? "Adding…"
              : scope === "global"
                ? addedGlobal
                  ? "Already global"
                  : "Add as global"
                : `Add to ${newAgentIds.length || ""} agent${newAgentIds.length === 1 ? "" : "s"}`.trim()}
          </button>
        </div>
      </div>
    </div>
  );
};

const MetricsGallery = () => {
  const navigate = useWsNavigate();
  const { currentWorkspace } = useAuth();
  const { agents } = useAgent();
  const { showToast } = useToast();
  const { setPageTitle, setParentPage, setShowBackButton } = usePageTitle();
  const canWrite = useCan("custom_metric", "write");

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [active, setActive] = useState(null); // template being added
  const [saving, setSaving] = useState(false);

  const agentName = useMemo(() => {
    const map = {};
    (agents || []).forEach((a) => (map[a.id] = a.name));
    return map;
  }, [agents]);

  useEffect(() => {
    setPageTitle("Metrics Gallery");
    setParentPage({ name: "Metrics", href: "/evaluation/metrics" });
    setShowBackButton(true);
    return () => {
      setPageTitle(null);
      setParentPage(null);
      setShowBackButton(false);
    };
  }, [setPageTitle, setParentPage, setShowBackButton]);

  const fetchGallery = useCallback(async () => {
    if (!currentWorkspace?.id) return;
    setLoading(true);
    try {
      const res = await metricsAPI.getGallery(currentWorkspace.id);
      setTemplates(res.data || []);
    } catch (e) {
      showToast("Failed to load gallery", "error");
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace, showToast]);

  useEffect(() => {
    fetchGallery();
  }, [fetchGallery]);

  const handleAdd = async ({ scope, agentIds }) => {
    if (!active) return;
    try {
      setSaving(true);
      await metricsAPI.addGalleryMetric(active.id, currentWorkspace.id, {
        scope,
        agentIds,
      });
      showToast(`“${active.name}” added to your workspace`, "success");
      setActive(null);
      fetchGallery(); // refresh applied-status so it can be added to more agents
    } catch (e) {
      showToast(e.response?.data?.error || "Failed to add metric", "error");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-32">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-foreground" />
      </div>
    );
  }

  return (
    <div className="px-4">
      {templates.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-center">
          <div className="mb-3 rounded-full bg-muted p-4">
            <LayoutGrid className="h-8 w-8 text-muted-foreground" />
          </div>
          <h2 className="ui-section-title">No gallery metrics yet</h2>
          <p className="ui-subtitle mt-1 max-w-sm">
            Curated templates will appear here once added.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {templates.map((t) => {
            const agentChips = (t.added_agent_ids || []).filter(
              (id) => agentName[id],
            );
            return (
              <Card
                key={t.id}
                className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <h3 className="truncate text-sm font-semibold text-foreground">
                      {t.name}
                    </h3>
                    <Badge variant={resultBadge(t.result_type)}>
                      {t.result_type}
                    </Badge>
                  </div>
                  {t.description && (
                    <p className="ui-meta mt-1 max-w-2xl">{t.description}</p>
                  )}
                  {(t.added_global || agentChips.length > 0) && (
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      <span className="text-[10px] font-medium uppercase tracking-wider text-muted-foreground">
                        Added to
                      </span>
                      {t.added_global ? (
                        <span className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-xs text-foreground">
                          <Globe className="size-3" /> All agents (global)
                        </span>
                      ) : (
                        agentChips.map((id) => (
                          <span
                            key={id}
                            className="inline-flex items-center gap-1 rounded-full border border-border bg-muted px-2 py-0.5 text-xs text-foreground"
                          >
                            <Users className="size-3" /> {agentName[id]}
                          </span>
                        ))
                      )}
                    </div>
                  )}
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  <button
                    type="button"
                    onClick={() =>
                      navigate(`/evaluation/metrics/gallery/${t.id}`, {
                        state: { template: t },
                      })
                    }
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-sm font-medium text-muted-foreground hover:bg-muted hover:text-foreground"
                  >
                    <Eye className="size-4" /> Preview
                  </button>
                  <button
                    type="button"
                    disabled={!canWrite}
                    onClick={() => setActive(t)}
                    className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-40"
                  >
                    {t.already_added ? (
                      <>
                        <Plus className="size-4" /> Add to more
                      </>
                    ) : (
                      <>
                        <Plus className="size-4" /> Add
                      </>
                    )}
                  </button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {active && (
        <AddMetricModal
          metric={active}
          agents={agents}
          saving={saving}
          onClose={() => !saving && setActive(null)}
          onConfirm={handleAdd}
        />
      )}
    </div>
  );
};

export default MetricsGallery;
